import { useMemo } from "react";
import { AsyncSelectValue } from "@/components/AsyncSelect";
import { useDebounce } from "@/hooks/use-debounce";
import { useProvidersList } from "@/queries/provider/setting/use-providers-list";
import useGetCataLogCode from "@/queries/catalog/use-get-catalog-code";
import { GetMedicalCodesTypeEnum } from "@/services";
import { DEF_PAGINATION, getXtenantId } from "@/utils/utils";
import { MEDICINE_CATALOG_PAGE_SIZE } from "../readmission-f2f-medication-constant";

export interface UseMedicationLookups {
  medicineOptions: AsyncSelectValue[];
  isMedicineLoading: boolean;
  setMedicineSearch: (value: string) => void;
  providerOptions: AsyncSelectValue[];
  isProviderLoading: boolean;
  setProviderSearch: (value: string) => void;
}

/** Drug catalog and provider options for the medication fields. */
export const useMedicationLookups = (
  isEnabled: boolean
): UseMedicationLookups => {
  const [medicineSearch, setMedicineSearch] = useDebounce<string>("");
  const [providerSearch, setProviderSearch] = useDebounce<string>("");

  const { data: medicines, isFetching: isMedicineLoading } = useGetCataLogCode({
    gridSorting: [],
    pagination: { page: 0, pageSize: MEDICINE_CATALOG_PAGE_SIZE },
    searchKey: medicineSearch,
    type: GetMedicalCodesTypeEnum.DrugCatalog,
    archive: false,
    active: true,
    enabled: isEnabled,
  });

  const { data: providers, isFetching: isProviderLoading } = useProvidersList({
    pagination: DEF_PAGINATION,
    xTENANTID: getXtenantId(),
    status: true,
    searchKey: providerSearch,
    enabled: isEnabled,
  });

  const medicineOptions = useMemo(
    () =>
      medicines?.data?.content?.map((it) => ({
        value: it.uuid ?? "",
        label: `${it.drugName} - ${it.description ?? ""}`,
      })) ?? [],
    [medicines]
  );

  const providerOptions = useMemo(
    () =>
      providers?.data?.content?.map((it) => ({
        value: it.uuid ?? "",
        label: `${it.firstName} ${it.lastName}`,
      })) ?? [],
    [providers]
  );

  return useMemo(
    () => ({
      medicineOptions,
      isMedicineLoading,
      setMedicineSearch,
      providerOptions,
      isProviderLoading,
      setProviderSearch,
    }),
    [
      medicineOptions,
      isMedicineLoading,
      setMedicineSearch,
      providerOptions,
      isProviderLoading,
      setProviderSearch,
    ]
  );
};
